import { Plus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Drawer } from '../../../components/Drawer';
import { Segmented } from '../../../components/Segmented';
import { SkeletonLines } from '../../../components/Skeleton';
import { EmptyState, ServerErrorState } from '../../../components/system-states/SystemState';
import { dhakaToday } from '../../../lib/dhaka-time';
import { useMediaQuery } from '../../../lib/use-media-query';
import { useNow } from '../../../lib/use-now';
import { useCurrentSession } from '../../../stores/session';
import { useColleagues, useCreateDelegation, useDelegations } from '../api/users-api';
import { ArrangeCoverForm } from '../components/delegation/ArrangeCoverForm';
import { CoverList } from '../components/delegation/CoverList';
import { CoverTimeline } from '../components/delegation/CoverTimeline';
import { sortCovers } from '../cover-dates';
import styles from './DelegationPage.module.css';

/** The phone (`--nr-bp-mobile`): no room for the timeline, and the form opens as a sheet from the button at the bottom (as drawn). */
const PHONE_MEDIA = '(max-width: 640px)';

type Scope = 'everyone' | 'mine';

/**
 * USER-05 / Admin · Delegation — cover for an absence. F2.11. Option A of `2-people/delegation.html` (Admin redesign 2026-11-02):
 * a five-week timeline of who is away and who covers, the list under it, and "Arrange cover" always open on the right.
 *
 * Without this, one approver's annual leave silently blocks every requisition routed to them, and nobody finds out for three
 * weeks — by which point the complaint is about the system rather than about the leave.
 *
 * A delegation routes approvals; it does NOT hand over the delegator's permissions. Granting somebody another person's whole
 * access for a fortnight is a privilege escalation nobody asked for and nobody would see.
 *
 * Anyone signed in arranges their own cover; "Everyone" and cover for somebody else are Admin's (the server enforces both).
 */
export function DelegationPage(): ReactNode {
  const session = useCurrentSession();
  const isAdmin = session?.roles.includes('admin') ?? false;
  // Admin's question is "is anyone away next week?", so Admin starts on everyone's (as drawn).
  const [scope, setScope] = useState<Scope>('everyone');
  const isEveryone = isAdmin && scope === 'everyone';
  const isPhone = useMediaQuery(PHONE_MEDIA);
  const [isSheetOpen, setSheetOpen] = useState(false);

  const delegations = useDelegations(isEveryone);
  const colleagues = useColleagues();
  const create = useCreateDelegation(session?.id ?? null);
  // The clock is an explicit input rather than a `Date.now()` read during render: "Active now" has to become "Finished" on its own.
  const now = useNow();
  const today = dhakaToday(new Date(now));
  const covers = sortCovers(delegations.data ?? [], now);
  const viewer = session ? { id: session.id, fullName: session.fullName } : null;

  const form =
    viewer === null ? null : (
      <ArrangeCoverForm
        viewer={viewer}
        isAdmin={isAdmin}
        colleagues={colleagues.data ?? []}
        now={now}
        onStart={(variables, handlers) => create.mutate(variables, handlers)}
        onStarted={isPhone ? () => setSheetOpen(false) : undefined}
      />
    );

  const list =
    delegations.isPending ? (
      <SkeletonLines count={4} />
    ) : delegations.isError && delegations.data === undefined ? (
      <ServerErrorState onRetry={() => void delegations.refetch()} />
    ) : covers.length === 0 ? (
      <EmptyState
        title="No cover arranged"
        body={
          isEveryone
            ? 'Nobody has cover arranged. Arrange one for whoever is going away, and their approvals keep moving.'
            : 'Arrange cover before you go away, and approvals keep moving while you are out.'
        }
        action={null}
        isInset
      />
    ) : (
      <CoverList covers={covers} now={now} viewerId={session?.id ?? null} isAdmin={isAdmin} />
    );

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.heading}>
          <h1 className={styles.title}>Delegation</h1>
          <p className={styles.lede}>
            Cover for approvals while someone is away. Only approvals move; nobody&apos;s own access changes.
          </p>
        </div>
        {isAdmin && (
          <Segmented<Scope>
            label="Whose cover"
            value={scope}
            options={[
              { value: 'everyone', label: 'Everyone' },
              { value: 'mine', label: 'Only mine' },
            ]}
            onChange={setScope}
          />
        )}
      </header>

      <div className={styles.layout}>
        {!isPhone && (
          <div className={styles.timeline}>
            {delegations.isPending ? (
              <Card title="Who is away, and who covers">
                <SkeletonLines count={3} />
              </Card>
            ) : (
              <CoverTimeline covers={covers} now={now} today={today} />
            )}
          </div>
        )}

        <section className={styles.list} aria-label="Cover arrangements">
          {list}
        </section>

        {!isPhone && <aside className={styles.form}>{form}</aside>}
      </div>

      {isPhone && (
        <>
          <div className={styles.phoneBar}>
            <Button variant="solid" icon={Plus} block onClick={() => setSheetOpen(true)}>
              Arrange cover
            </Button>
          </div>
          <Drawer isOpen={isSheetOpen} onClose={() => setSheetOpen(false)} title="Arrange cover">
            {form}
          </Drawer>
        </>
      )}
    </div>
  );
}
